/**
 * Service Information Download Section Component
 * 
 * Section for viewing and downloading service information from UAT/Production environments.
 * Displays service-level metadata including dates, routes, shifts, and vehicles.
 */

import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Alert,
  AlertTitle,
  Button,
  CircularProgress,
  LinearProgress,
  Stack,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
  Checkbox,
  FormControlLabel,
} from '@mui/material';

import { 
  fetchOperationsSolutions, 
  fetchNetworkSettings,
  fetchServiceSettings,
  type UATNetwork, 
  type UATOperationsSolutionsResponse, 
  type UATOperationsSolution,
  type UATNetworkSettingsResponse,
  type UATNetworkSettings,
  type UATServiceSettingsResponse,
  type UATODBServiceInformation
} from '../../../services/fixedRouteUatClient';
import { type Environment, getEnvironmentDescriptor, getEnvironmentDisplayName } from './EnvironmentSelector';
import { getCachedOperationsSolutions, setCachedOperationsSolutions } from './operationsSolutionsCache';
import { getCachedODBServiceInfoList, setCachedODBServiceInfoList } from './odbServiceInfoCache';
import { extractUATODBServiceInformation, importUATODBServiceInformation, type ODBServiceInformationImportResult } from '../../../services/fixedRouteUatImporter';
import { AppContext } from '../../../context/context';

import { extractUATServices } from '../../../services/fixedRouteUatImporter';
import type { Service } from '../../../types/service';
import { getCachedServicesList, setCachedServicesList } from './servicesCache';

import {
  extractUATStops,
  extractUATRoutes,
  extractUATFleets,
  importUATStops,
  importUATRoutes,
  type StopImportResult,
  type RouteImportResult,
} from '../../../services/fixedRouteUatImporter';
import { useFixedRoutes } from '../../../context/FixedRoutesContext';
import { useEditLayer } from '../../layer_panel/functions/layerFunctions';
import { v4 as uuid4 } from 'uuid';
import type { Vehicle, Fleet } from './FleetMenu';
import { drawerPrimaryActionSx, drawerTertiaryActionSx } from '../../../theme/componentStyles';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../utils/iconFallbacks';
interface ServiceInformationDownloadSectionProps {
  jwtToken: string;
  environment: Environment;
  serviceDate: string;
  timezone: string;
  onServiceDateChange: (date: string) => void;
  onTimezoneChange: (timezone: string) => void;
  networks: UATNetwork[];
  isLoadingNetworks: boolean;
  networkError: string | null;
  selectedProductId: string;
  selectedNetworkId: number | null;
  onProductChange: (productId: string) => void;
  onLoadNetworks: () => void;
  downloadAcknowledged: boolean;
  onDownloadAcknowledgedChange: (acknowledged: boolean) => void;
}

export const ServiceInformationDownloadSection: React.FC<ServiceInformationDownloadSectionProps> = ({
  jwtToken,
  environment,
  serviceDate,
  timezone,
  onServiceDateChange,
  onTimezoneChange,
  networks,
  isLoadingNetworks,
  networkError,
  selectedProductId,
  selectedNetworkId,
  onProductChange,
  onLoadNetworks,
  downloadAcknowledged,
  onDownloadAcknowledgedChange,
}) => {
  const { t } = useTranslation('mapDownload');
  const context = useContext(AppContext);
  const { stops: existingStops, routes: existingRoutes, addStop, updateStop, addRoute } = useFixedRoutes();
  const editLayer = useEditLayer();

  // Download state (FR service information)
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [isLoadingNetworkSettings, setIsLoadingNetworkSettings] = useState<boolean>(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [networkSettingsError, setNetworkSettingsError] = useState<string | null>(null);
  const [operationsSolutions, setOperationsSolutions] = useState<UATOperationsSolutionsResponse | null>(null);
  const [networkSettings, setNetworkSettings] = useState<UATNetworkSettingsResponse | null>(null);
  const [expandedServiceIds, setExpandedServiceIds] = useState<Set<string>>(new Set());
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['networkSettings']));
  const [selectedFilters, setSelectedFilters] = useState<Set<'services' | 'routes' | 'shifts' | 'vehicles' | 'drivers'>>(new Set());
  
  // Download state (ODB service information)
  const [isDownloadingODB, setIsDownloadingODB] = useState<boolean>(false);
  const [downloadErrorODB, setDownloadErrorODB] = useState<string | null>(null);
  const [odbServiceSettings, setOdbServiceSettings] = useState<UATServiceSettingsResponse | null>(null);
  const [odbServiceInfoList, setOdbServiceInfoList] = useState<UATODBServiceInformation[]>([]);
  const [selectedODBServiceInfoIds, setSelectedODBServiceInfoIds] = useState<Set<string>>(new Set());
  const [odbServiceInfoSearchQuery, setOdbServiceInfoSearchQuery] = useState<string>('');
  const [expandedODBServiceInfoIds, setExpandedODBServiceInfoIds] = useState<Set<string>>(new Set());
  
  // Import state (FR service information)
  const [isImporting, setIsImporting] = useState<boolean>(false);
  const [importProgress, setImportProgress] = useState<number>(0);
  const [importStep, setImportStep] = useState<string>('');
  const [importError, setImportError] = useState<string | null>(null);
  const [importResults, setImportResults] = useState<{
    stops?: { created: number; updated: number; skipped: number };
    routes?: { created: number; skipped: number };
    vehicles?: { created: number };
    servicesCreated?: number;
  } | null>(null);

  // Import state (ODB service information)
  const [isImportingODB, setIsImportingODB] = useState<boolean>(false);
  const [importProgressODB, setImportProgressODB] = useState<number>(0);
  const [importStepODB, setImportStepODB] = useState<string>('');
  const [importErrorODB, setImportErrorODB] = useState<string | null>(null);
  const [importResultsODB, setImportResultsODB] = useState<ODBServiceInformationImportResult[] | null>(null);

  // Service Settings state (from ServicesDownloadSection)
  const [servicesResponse, setServicesResponse] = useState<UATServiceSettingsResponse | null>(null);
  const [previewServices, setPreviewServices] = useState<Service[]>([]);
  const [selectedServiceIds, setSelectedServiceIds] = useState<Set<string>>(new Set());
  const [serviceSearchQuery, setServiceSearchQuery] = useState<string>('');

  // Reset state when environment, JWT token, product, or network changes
  useEffect(() => {
    setOperationsSolutions(null);
    setNetworkSettings(null);
    setDownloadError(null);
    setNetworkSettingsError(null);
    
    // Reset ODB service information state
    setOdbServiceSettings(null);
    setOdbServiceInfoList([]);
    setSelectedODBServiceInfoIds(new Set());
    setDownloadErrorODB(null);
    setImportResultsODB(null);
    setImportErrorODB(null);
    
    // Reset service settings state
    setServicesResponse(null);
    setPreviewServices([]);
    setSelectedServiceIds(new Set());
  }, [jwtToken, environment, selectedProductId, selectedNetworkId]);

  // Auto-load network settings when product is selected
  useEffect(() => {
    if (
      downloadAcknowledged &&
      jwtToken.trim() &&
      selectedProductId &&
      !isLoadingNetworkSettings
    ) {
      handleLoadNetworkSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadAcknowledged, jwtToken, selectedProductId, environment]);

  // Update timezone when network settings are loaded
  useEffect(() => {
    if (networkSettings?.data?.networks && networkSettings.data.networks.length > 0) {
      const networkSettingsData = networkSettings.data.networks[0]?.networkSettings;
      if (networkSettingsData?.timezone && networkSettingsData.timezone !== timezone) {
        onTimezoneChange(networkSettingsData.timezone);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkSettings]);

  // Get timezone from network settings (timezone-agnostic approach)
  const effectiveTimezone = networkSettings?.data?.networks?.[0]?.networkSettings?.timezone || timezone;

  /**
   * Format date/time using the network's timezone (not user's local timezone)
   */
  const formatTimeInNetworkTimezone = (dateString: string, includeDate: boolean = false): string => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const options: Intl.DateTimeFormatOptions = {
        timeZone: effectiveTimezone,
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      };
      if (includeDate) {
        options.year = 'numeric';
        options.month = '2-digit';
        options.day = '2-digit';
      }
      return date.toLocaleString('en-US', options);
    } catch (error) {
      console.warn('[ServiceInformationDownloadSection] Failed to format time:', error);
      return dateString;
    }
  };

  // Clear preview data when checkbox is unchecked (no auto-download - manual download only)
  useEffect(() => {
    if (!downloadAcknowledged) {
      setOperationsSolutions(null);
      setDownloadError(null);
      setOdbServiceInfoList([]);
      setDownloadErrorODB(null);
    }
  }, [downloadAcknowledged]);

  const handleLoadNetworkSettings = async () => {
    if (!jwtToken.trim() || !selectedProductId) return;

    setIsLoadingNetworkSettings(true);
    setNetworkSettingsError(null);

    try {
      const response = await fetchNetworkSettings(jwtToken, selectedProductId, environment);
      setNetworkSettings(response);
      
      // Update timezone from network settings if available
      if (response?.data?.networks && response.data.networks.length > 0) {
        const networkSettings = response.data.networks[0]?.networkSettings;
        if (networkSettings?.timezone) {
          onTimezoneChange(networkSettings.timezone);
        }
      }
    } catch (error) {
      let errorMessage = t('serviceInfo.errors.networkSettingsLoadFailed');
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      setNetworkSettingsError(errorMessage);
      console.warn('[ServiceInformationDownloadSection] Failed to load network settings:', error);
    } finally {
      setIsLoadingNetworkSettings(false);
    }
  };

  /**
   * Download ODB service information from UAT
   */
  const handleDownloadODB = async () => {
    if (!jwtToken.trim()) { setDownloadErrorODB(t('common.errors.jwtRequired')); return; }
    if (!selectedProductId) { setDownloadErrorODB(t('common.errors.productRequired')); return; }
    if (!serviceDate) { setDownloadErrorODB(t('common.errors.serviceDateRequired')); return; }
    
    // Use timezone from network settings (timezone-agnostic)
    const effectiveTimezone = networkSettings?.data?.networks?.[0]?.networkSettings?.timezone || timezone;
    if (!effectiveTimezone.trim()) {
      setDownloadErrorODB(t('common.errors.timezoneRequired'));
      return;
    }

    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(serviceDate)) { setDownloadErrorODB(t('common.errors.invalidDateFormat')); return; }

    // Derive date range from serviceDate (like GeofencesDownloadSection)
    const startDate = serviceDate;
    const endDate = (() => {
      const date = new Date(serviceDate);
      date.setDate(date.getDate() + 30);
      return date.toISOString().split('T')[0];
    })();

    // Check cache first
    const cachedODBServiceInfo = getCachedODBServiceInfoList(environment, selectedProductId, startDate, endDate);
    if (cachedODBServiceInfo) {
      setOdbServiceInfoList(cachedODBServiceInfo);
      setSelectedODBServiceInfoIds(new Set(cachedODBServiceInfo.map(si => si.id)));
      setDownloadErrorODB(null);
      return;
    }

    setIsDownloadingODB(true);
    setDownloadErrorODB(null);
    setOdbServiceSettings(null);
    setOdbServiceInfoList([]);

    try {
      // Fetch service settings (same endpoint as geofences/hubs)
      const response = await fetchServiceSettings(
        jwtToken,
        selectedProductId,
        startDate,
        endDate,
        environment
      );

      setOdbServiceSettings(response);

      // Extract ODB service information from service settings
      const odbServiceInfo = extractUATODBServiceInformation(response);
      setOdbServiceInfoList(odbServiceInfo);

      // Select all by default
      setSelectedODBServiceInfoIds(new Set(odbServiceInfo.map(si => si.id)));

      // Cache the extracted data
      setCachedODBServiceInfoList(environment, selectedProductId, startDate, endDate, odbServiceInfo);

      setDownloadErrorODB(null);
    } catch (error) {
      console.error('[ServiceInformationDownloadSection] ODB download error:', error);
      let errorMessage = t('serviceInfo.errors.downloadODBBase', { environment: getEnvironmentDisplayName(environment) });
      if (error instanceof Error) {
        errorMessage = error.message || String(error);
        if (error.message.includes('401') || error.message.includes('Authentication')) {
          errorMessage = t('common.errors.authFailed');
        } else if (error.message.includes('404')) {
          errorMessage = t('serviceInfo.errors.notFoundODB');
        } else if (error.message.includes('429')) {
          errorMessage = t('common.errors.tooManyRequests');
        } else if (error.message.includes('500') || error.message.includes('502') || error.message.includes('503')) {
          errorMessage = t('common.errors.serverError', { environment: getEnvironmentDisplayName(environment) });
        }
      } else {
        errorMessage = t('serviceInfo.errors.downloadODBString', { value: String(error) });
      }
      setDownloadErrorODB(errorMessage);
    } finally {
      setIsDownloadingODB(false);
    }
  };

  const handleDownload = async () => {
    if (!jwtToken.trim()) { setDownloadError(t('common.errors.jwtRequired')); return; }
    if (!selectedProductId) { setDownloadError(t('common.errors.productRequired')); return; }
    if (!serviceDate) { setDownloadError(t('common.errors.serviceDateRequired')); return; }

    // Use timezone from network settings (timezone-agnostic)
    const effectiveTimezone = networkSettings?.data?.networks?.[0]?.networkSettings?.timezone || timezone;
    if (!effectiveTimezone.trim()) {
      setDownloadError(t('common.errors.timezoneRequired'));
      return;
    }

    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(serviceDate)) { setDownloadError(t('common.errors.invalidDateFormat')); return; }

    // Initialize loading states
    setIsDownloading(false);
    setIsDownloadingODB(false);
    setDownloadError(null);
    setDownloadErrorODB(null);

    // Check cache first for FR service information
    const cachedResponse = getCachedOperationsSolutions(environment, selectedProductId, serviceDate, effectiveTimezone);
    if (cachedResponse) {
      setOperationsSolutions(cachedResponse);
      setDownloadError(null);
    } else {
      setIsDownloading(true);
      setOperationsSolutions(null);
    }

    // Derive date range for ODB service information
    const startDate = serviceDate;
    const endDate = (() => {
      const date = new Date(serviceDate);
      date.setDate(date.getDate() + 30);
      return date.toISOString().split('T')[0];
    })();

    // Always try to download ODB service information in parallel (check cache first)
    const cachedODBServiceInfo = getCachedODBServiceInfoList(environment, selectedProductId, startDate, endDate);
    if (cachedODBServiceInfo) {
      setOdbServiceInfoList(cachedODBServiceInfo);
      setSelectedODBServiceInfoIds(new Set(cachedODBServiceInfo.map(si => si.id)));
      setDownloadErrorODB(null);
      setIsDownloadingODB(false);
    } else {
      setIsDownloadingODB(true);
      setOdbServiceSettings(null);
      setOdbServiceInfoList([]);
    }

    // Fetch both FR and ODB service information in parallel
    const fetchPromises: Promise<void>[] = [];

    // Fetch FR service information if not cached
    if (!cachedResponse) {
      fetchPromises.push(
        (async () => {
    try {
      const response = await fetchOperationsSolutions(
        jwtToken,
        selectedProductId,
        serviceDate,
        effectiveTimezone,
        environment
      );

      // Cache the response
      setCachedOperationsSolutions(environment, selectedProductId, serviceDate, effectiveTimezone, response);

      setOperationsSolutions(response);
      setDownloadError(null);
    } catch (error) {
            let errorMessage = t('serviceInfo.errors.downloadFRBase', { environment: getEnvironmentDisplayName(environment) });
      if (error instanceof Error) {
        errorMessage = error.message;
        if (error.message.includes('401') || error.message.includes('Authentication')) {
          errorMessage = t('common.errors.authFailed');
        } else if (error.message.includes('404')) {
                errorMessage = t('serviceInfo.errors.notFoundFR');
        } else if (error.message.includes('429')) {
          errorMessage = t('common.errors.tooManyRequests');
        } else if (error.message.includes('500') || error.message.includes('502') || error.message.includes('503')) {
          errorMessage = t('common.errors.serverError', { environment: getEnvironmentDisplayName(environment) });
        }
      }
      setDownloadError(errorMessage);
    } finally {
      setIsDownloading(false);
          }
        })()
      );
    }

    // Fetch ODB service information and service settings if not cached
    if (!cachedODBServiceInfo) {
      fetchPromises.push(
        (async () => {
          try {
            const response = await fetchServiceSettings(
              jwtToken,
              selectedProductId,
              startDate,
              endDate,
              environment
            );

            setOdbServiceSettings(response);

            // Extract ODB service information from service settings
            const odbServiceInfo = extractUATODBServiceInformation(response);
            setOdbServiceInfoList(odbServiceInfo);

            // Select all by default
            setSelectedODBServiceInfoIds(new Set(odbServiceInfo.map(si => si.id)));

            // Cache the extracted data
            setCachedODBServiceInfoList(environment, selectedProductId, startDate, endDate, odbServiceInfo);

            // Also extract and set service settings for import
            const effectiveTimezone = networkSettings?.data?.networks?.[0]?.networkSettings?.timezone || timezone;
            const cachedServices = getCachedServicesList(environment, selectedProductId, startDate, endDate, effectiveTimezone);
            if (cachedServices) {
              setServicesResponse(cachedServices);
              const services = extractUATServices(cachedServices, getEnvironmentDescriptor(environment), selectedProductId, serviceDate);
              console.log('[ServiceInformationDownloadSection] Extracted services from cache:', services.length, services);
              setPreviewServices(services);
              const serviceIds = new Set(services.map(s => s.id));
              console.log('[ServiceInformationDownloadSection] Auto-selected service IDs:', serviceIds.size, Array.from(serviceIds));
              setSelectedServiceIds(serviceIds);
            } else {
              // Extract service settings from the same response
              const services = extractUATServices(response, getEnvironmentDescriptor(environment), selectedProductId, serviceDate);
              console.log('[ServiceInformationDownloadSection] Extracted services from response:', services.length, services);
              setServicesResponse(response);
              setPreviewServices(services);
              const serviceIds = new Set(services.map(s => s.id));
              console.log('[ServiceInformationDownloadSection] Auto-selected service IDs:', serviceIds.size, Array.from(serviceIds));
              setSelectedServiceIds(serviceIds);
              // Cache service settings
              setCachedServicesList(environment, selectedProductId, startDate, endDate, effectiveTimezone, response);
            }

            setDownloadErrorODB(null);
          } catch (error) {
            console.error('[ServiceInformationDownloadSection] ODB download error (parallel):', error);
            let errorMessage = t('serviceInfo.errors.downloadODBBase', { environment: getEnvironmentDisplayName(environment) });
            if (error instanceof Error) {
              errorMessage = error.message || String(error);
              if (error.message.includes('401') || error.message.includes('Authentication')) {
                errorMessage = t('common.errors.authFailed');
              } else if (error.message.includes('404')) {
                errorMessage = t('serviceInfo.errors.notFoundODB');
              } else if (error.message.includes('429')) {
                errorMessage = t('common.errors.tooManyRequests');
              } else if (error.message.includes('500') || error.message.includes('502') || error.message.includes('503')) {
                errorMessage = t('common.errors.serverError', { environment: getEnvironmentDisplayName(environment) });
              }
            } else {
              errorMessage = t('serviceInfo.errors.downloadODBString', { value: String(error) });
            }
            setDownloadErrorODB(errorMessage);
          } finally {
            setIsDownloadingODB(false);
          }
        })()
      );
    } else {
      // If ODB is cached, still try to get service settings from cache or fetch them
      const effectiveTimezone = networkSettings?.data?.networks?.[0]?.networkSettings?.timezone || timezone;
      const cachedServices = getCachedServicesList(environment, selectedProductId, startDate, endDate, effectiveTimezone);
      if (cachedServices) {
        setServicesResponse(cachedServices);
        const services = extractUATServices(cachedServices, getEnvironmentDescriptor(environment), selectedProductId, serviceDate);
        console.log('[ServiceInformationDownloadSection] Extracted services from cache (ODB cached):', services.length, services);
        setPreviewServices(services);
        const serviceIds = new Set(services.map(s => s.id));
        console.log('[ServiceInformationDownloadSection] Auto-selected service IDs (ODB cached):', serviceIds.size, Array.from(serviceIds));
        setSelectedServiceIds(serviceIds);
      } else {
        // Fetch service settings if not cached
        fetchPromises.push(
          (async () => {
            try {
              const response = await fetchServiceSettings(
                jwtToken,
                selectedProductId,
                startDate,
                endDate,
                environment
              );

              const effectiveTimezone = networkSettings?.data?.networks?.[0]?.networkSettings?.timezone || timezone;
              const services = extractUATServices(response, getEnvironmentDescriptor(environment), selectedProductId, serviceDate);
              console.log('[ServiceInformationDownloadSection] Extracted services from fetch (ODB cached):', services.length, services);
              setServicesResponse(response);
              setPreviewServices(services);
              const serviceIds = new Set(services.map(s => s.id));
              console.log('[ServiceInformationDownloadSection] Auto-selected service IDs (ODB cached, fetched):', serviceIds.size, Array.from(serviceIds));
              setSelectedServiceIds(serviceIds);
              // Cache service settings
              setCachedServicesList(environment, selectedProductId, startDate, endDate, effectiveTimezone, response);
            } catch (error) {
              console.error('[ServiceInformationDownloadSection] Service settings download error:', error);
              // Don't set error - service settings are optional
            }
          })()
        );
      }
    }

    await Promise.allSettled(fetchPromises);
  };

  /**
   * Handle import of selected ODB service information
   */
  const handleImportODB = async () => {
    if (selectedODBServiceInfoIds.size === 0) {
      setImportErrorODB(t('serviceInfo.errors.noSelectionODB'));
      return;
    }

    setIsImportingODB(true);
    setImportErrorODB(null);
    setImportProgressODB(0);
    setImportStepODB(t('serviceInfo.steps.initializing'));

    try {
      setImportStepODB(t('serviceInfo.steps.processingSelected'));
      setImportProgressODB(20);

      // Get selected service information
      const selectedServiceInfo = odbServiceInfoList.filter(si => selectedODBServiceInfoIds.has(si.id));
      
      setImportStepODB(t('serviceInfo.steps.importingEntries', { count: selectedServiceInfo.length }));
      setImportProgressODB(40);

      // TODO: Get existing service information from Atlas (TBD - depends on storage approach)
      const existingServiceInfo: any[] = [];

      // Import service information
      const results = importUATODBServiceInformation(selectedServiceInfo, existingServiceInfo);
      
      setImportProgressODB(80);
      setImportStepODB(t('serviceInfo.steps.finalizing'));

      setImportResultsODB(results);
      setImportProgressODB(100);
      setImportStepODB(t('serviceInfo.steps.complete'));

      // Clear selections after successful import
      setSelectedODBServiceInfoIds(new Set());

    } catch (error) {
      console.error('[ServiceInformationDownloadSection] ODB import error:', error);
      let errorMessage = t('serviceInfo.errors.importODBGeneric');
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      setImportErrorODB(errorMessage);
    } finally {
      setIsImportingODB(false);
    }
  };

  const selectedProduct = networks.find(p => p.productId === selectedProductId);

  // Calculate summary statistics
  const serviceEntries = operationsSolutions?.data?.entries || [];
  const totalShifts = serviceEntries.reduce((sum, entry) => sum + (entry.shiftSolutions?.length || 0), 0);
  const totalRoutes = new Set(
    serviceEntries.flatMap(entry => 
      entry.shiftSolutions?.map(shift => shift.route?.id).filter(Boolean) || []
    )
  ).size;
  const totalVehicles = new Set(
    serviceEntries.flatMap(entry => 
      entry.shiftSolutions?.map(shift => shift.vehicle?.id).filter(Boolean) || []
    )
  ).size;
  const totalDrivers = new Set(
    serviceEntries.flatMap(entry => 
      entry.shiftSolutions?.map(shift => shift.driver?.id).filter(Boolean) || []
    )
  ).size;

  /**
   * Convert UAT shift to OperatingPeriod format
   */
  const convertShiftToOperatingPeriod = (shift: any): any => {
    if (!shift.startDate || !shift.endDate) {
      return null;
    }

    const startDate = new Date(shift.startDate);
    const endDate = new Date(shift.endDate);

    return {
      start: `${String(startDate.getHours()).padStart(2, '0')}:${String(startDate.getMinutes()).padStart(2, '0')}`,
      end: `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`,
      breaks: [],
    };
  };

  /**
   * Convert UAT vehicle capacities to standard/wheelchair seats
   */
  const extractVehicleCapacities = (capacities: any[]): { standardSeats: number; wheelchairSeats: number } => {
    let standardSeats = 0;
    let wheelchairSeats = 0;

    if (capacities && Array.isArray(capacities)) {
      capacities.forEach((cap: any) => {
        if (cap.type === 'standard' || cap.type === 'STANDARD') {
          standardSeats += cap.count || 0;
        } else if (cap.type === 'wheelchair' || cap.type === 'WHEELCHAIR' || cap.type === 'accessible') {
          wheelchairSeats += cap.count || 0;
        }
      });
    }

    // Default values if no capacities found
    if (standardSeats === 0 && wheelchairSeats === 0) {
      standardSeats = 4; // Default
    }

    return { standardSeats, wheelchairSeats };
  };

  /**
   * Handle import of selected data types onto the map
   */
  const handleDownloadServiceInfo = async () => {
    console.log('[ServiceInformationDownloadSection] Import button clicked. Selected filters:', selectedFilters.size, 'Selected service IDs:', selectedServiceIds.size);
    console.log('[ServiceInformationDownloadSection] Preview services:', previewServices.length, previewServices);
    console.log('[ServiceInformationDownloadSection] Selected service IDs:', Array.from(selectedServiceIds));
    
    // Allow import if either service information filters are selected OR service settings are selected
    if (selectedFilters.size === 0 && selectedServiceIds.size === 0) {
      setDownloadError(t('serviceInfo.errors.selectAtLeastOne'));
      return;
    }

    // Only require operationsSolutions if routes/vehicles are being imported
    if (selectedFilters.size > 0 && (!operationsSolutions || serviceEntries.length === 0)) {
      setDownloadError(t('serviceInfo.errors.noServiceInfoAvailable'));
      return;
    }

    const filters = Array.from(selectedFilters);
    setIsImporting(true);
    setImportError(null);
    setImportProgress(0);
    setImportStep(t('serviceInfo.steps.initializing'));

    try {
      const results: {
        stops?: { created: number; updated: number; skipped: number };
        routes?: { created: number; skipped: number };
        vehicles?: { created: number };
        servicesCreated?: number;
      } = {};

      // Import routes and stops if routes filter is selected
      if (filters.includes('routes')) {
        if (!operationsSolutions) {
          setImportError(t('serviceInfo.errors.operationsSolutionsUnavailable'));
          return;
        }
        setImportStep(t('serviceInfo.steps.extractingStops', { environment: getEnvironmentDisplayName(environment) }));
        setImportProgress(10);
        const uatStops = extractUATStops(operationsSolutions);
        
        setImportStep(t('serviceInfo.steps.processingStops', { count: uatStops.size }));
        setImportProgress(30);
        const stopResults = importUATStops(uatStops, existingStops);

        setImportStep(t('serviceInfo.steps.applyingStops'));
        setImportProgress(50);

        // Apply stop imports with metadata enhancement
        const updatedStopIds = new Set<string>();
        for (const result of stopResults) {
          if (result.action === 'created') {
            const enhancedStop = {
              ...result.stop,
              uatMetadata: {
                originalId: result.stop.uatStopId || result.stop.id,
                originalName: result.stop.name,
                originalLabel: result.stop.label,
                importedAt: Date.now(),
                importedFrom: getEnvironmentDescriptor(environment),
                productId: selectedProductId,
              },
            };
            addStop(enhancedStop);
            updatedStopIds.add(enhancedStop.id);
          } else if (result.action === 'updated') {
            const enhancedStop = {
              ...result.stop,
              uatMetadata: {
                originalId: result.stop.uatStopId || result.stop.id,
                originalName: result.stop.name,
                originalLabel: result.stop.label,
                importedAt: Date.now(),
                importedFrom: getEnvironmentDescriptor(environment),
                productId: selectedProductId,
              },
            };
            updateStop(enhancedStop);
            updatedStopIds.add(enhancedStop.id);
          }
        }

        results.stops = {
          created: stopResults.filter(r => r.action === 'created').length,
          updated: stopResults.filter(r => r.action === 'updated').length,
          skipped: stopResults.filter(r => r.action === 'skipped').length,
        };

        // Import routes
        if (!operationsSolutions) {
          setImportError(t('serviceInfo.errors.operationsSolutionsUnavailable'));
          return;
        }
        setImportStep(t('serviceInfo.steps.extractingRoutes', { environment: getEnvironmentDisplayName(environment) }));
        setImportProgress(60);
        const allUatRoutes = extractUATRoutes(operationsSolutions, effectiveTimezone);

        setImportStep(t('serviceInfo.steps.processingRoutes', { count: allUatRoutes.length }));
        setImportProgress(70);

        // Build updated stops list
        const updatedStopsMap = new Map<string, any>();
        existingStops.forEach(stop => {
          updatedStopsMap.set(stop.id, stop);
        });
        stopResults.forEach(result => {
          if (result.action === 'created' || result.action === 'updated') {
            updatedStopsMap.set(result.stop.id, result.stop);
          }
        });
        const updatedStops = Array.from(updatedStopsMap.values());

        setImportStep(t('serviceInfo.steps.mappingRoutes'));
        setImportProgress(80);
        const routeResults = importUATRoutes(allUatRoutes, updatedStops, existingRoutes, uatStops, effectiveTimezone);

        setImportStep(t('serviceInfo.steps.applyingRoutes'));
        setImportProgress(90);

        // Apply route imports
        for (const result of routeResults) {
          if (result.action === 'created') {
            addRoute(result.route);
          }
        }

        results.routes = {
          created: routeResults.filter(r => r.action === 'created').length,
          skipped: routeResults.filter(r => r.action === 'skipped').length,
        };

        // Center map on imported routes and stops
        try {
          const allLatLngs: [number, number][] = [];
          
          stopResults.forEach(result => {
            if (result.stop.coordinates && 
                result.stop.coordinates.lat !== 0 && 
                result.stop.coordinates.lng !== 0 && 
                !isNaN(result.stop.coordinates.lat) && 
                !isNaN(result.stop.coordinates.lng)) {
              allLatLngs.push([result.stop.coordinates.lat, result.stop.coordinates.lng]);
            }
          });
          
          routeResults.forEach(result => {
            if (result.action === 'created' && result.route.geometry?.coordinates) {
              const routeCoords = result.route.geometry.coordinates;
              if (Array.isArray(routeCoords) && routeCoords.length > 0) {
                routeCoords.forEach((coord: any) => {
                  if (Array.isArray(coord) && coord.length >= 2) {
                    const lat = coord[0];
                    const lng = coord[1];
                    if (typeof lat === 'number' && typeof lng === 'number' && 
                        !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
                      allLatLngs.push([lat, lng]);
                    }
                  }
                });
              }
            }
          });
          
          if (allLatLngs.length > 0) {
            const shapes: Array<{ type: 'point'; coordinates: [number, number]; hidden?: boolean }> = [];
            allLatLngs.forEach(coord => {
              shapes.push({
                type: 'point',
                coordinates: coord,
                hidden: false
              });
            });
            
            const event = new CustomEvent('zoomToLayer', {
              detail: { shapes }
            });
            window.dispatchEvent(event);
          }
        } catch (error) {
          console.warn('[ServiceInformationDownloadSection] Failed to center map:', error);
        }
      }

      // Import vehicles if vehicles filter is selected
      if (filters.includes('vehicles') && context?.global?.setFleets) {
        if (!operationsSolutions) {
          setImportError(t('serviceInfo.errors.operationsSolutionsUnavailable'));
          return;
        }
        setImportStep(t('serviceInfo.steps.extractingVehicles', { environment: getEnvironmentDisplayName(environment) }));
        setImportProgress(50);
        const fleets = extractUATFleets(operationsSolutions);

        setImportStep(t('serviceInfo.steps.processingVehicles', { count: fleets.size }));
        setImportProgress(70);

        const allVehicles: Vehicle[] = [];
        const fleetArray = Array.from(fleets.values());

        for (const uatFleet of fleetArray) {
          const { standardSeats, wheelchairSeats } = extractVehicleCapacities(uatFleet.capacities || []);

          const operatingHours: any[] = [];
          if (uatFleet.shifts && Array.isArray(uatFleet.shifts)) {
            uatFleet.shifts.forEach((shift: any) => {
              const operatingPeriod = convertShiftToOperatingPeriod(shift);
              if (operatingPeriod) {
                operatingHours.push(operatingPeriod);
              }
            });
          }

          const vehicle: Vehicle = {
            id: `vehicle-${uuid4()}`,
            name: uatFleet.name || t('serviceInfo.vehicleFallbackName', { id: uatFleet.id.substring(0, 8) }),
            standardSeats,
            wheelchairSeats,
            startLocation: '',
            endLocation: '',
            operatingHours,
            uatVehicleId: uatFleet.id,
            uatMetadata: {
              originalId: uatFleet.id,
              originalName: uatFleet.name,
              capacities: uatFleet.capacities,
              picture: uatFleet.picture,
              shifts: uatFleet.shifts,
              importedAt: Date.now(),
              importedFrom: getEnvironmentDescriptor(environment),
              productId: selectedProductId,
              serviceDate: serviceDate,
            },
          };

          allVehicles.push(vehicle);
        }

        if (allVehicles.length > 0) {
          setImportStep(t('serviceInfo.steps.creatingFleet'));
          setImportProgress(85);

          const productName = selectedProduct?.productName || 'UAT';
          const dateStr = serviceDate ? new Date(serviceDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
          const fleetName = dateStr
            ? t('serviceInfo.fleetNameWithDate', { productName, date: dateStr })
            : t('serviceInfo.fleetNameTemplate', { productName });

          const newFleet: Fleet = {
            id: `fleet-${uuid4()}`,
            name: fleetName,
            vehicles: allVehicles,
            uatMetadata: {
              importedAt: Date.now(),
              importedFrom: getEnvironmentDescriptor(environment),
              productId: selectedProductId,
              serviceDate: serviceDate,
              sourceVehicleIds: fleetArray.map(f => f.id),
            },
          };

          const existingFleets = context.global.fleets || [];
          context.global.setFleets([...existingFleets, newFleet]);

          results.vehicles = {
            created: allVehicles.length,
          };
        }
      }

      // Import service settings if any are selected
      if (selectedServiceIds.size > 0 && context?.global?.setServices) {
        setImportStep(t('serviceInfo.steps.importingSettings', { count: selectedServiceIds.size }));
        setImportProgress(selectedFilters.size > 0 ? 90 : 50);
        
        const selectedServices = previewServices.filter(s => selectedServiceIds.has(s.id));
        console.log('[ServiceInformationDownloadSection] Importing services:', selectedServices.length, selectedServices);
        console.log('[ServiceInformationDownloadSection] Current services in context before import:', context.global.services?.length || 0);
        
        // Use the setServices function from context
        const currentServices = context.global.services || [];
        const updatedServices = [...currentServices, ...selectedServices];
        console.log('[ServiceInformationDownloadSection] Services after import:', updatedServices.length, updatedServices);
        
        context.global.setServices(updatedServices);
        
        // Verify the update
        setTimeout(() => {
          console.log('[ServiceInformationDownloadSection] Services in context after update:', context.global.services?.length || 0);
        }, 100);
        
        results.servicesCreated = selectedServices.length;
        
        // Clear service settings selection after import
        setSelectedServiceIds(new Set());
      }

      setImportProgress(100);
      setImportStep(t('serviceInfo.steps.complete'));
      setImportResults(results);

      // Clear selection after successful import
      setSelectedFilters(new Set());

    } catch (error) {
      console.error('[ServiceInformationDownloadSection] Import error:', error);
      setImportError(error instanceof Error ? error.message : t('serviceInfo.errors.importGeneric'));
    } finally {
      setIsImporting(false);
    }
  };

  // Show placeholder if consent not acknowledged
  if (!downloadAcknowledged) {
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        width: '100%',
        flex: 1,
        minHeight: 0,
        p: 2,
        boxSizing: 'border-box',
        overflow: 'hidden'
      }}>
        <img
          src="/icons/consent-img.png"
          alt={t('common.consentImageAlt')}
          style={{ 
            maxWidth: '100%',
            maxHeight: '100%',
            width: 'auto',
            height: 'auto',
            objectFit: 'contain',
            objectPosition: 'center center'
          }} 
        />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', overflowX: 'hidden' }}>
      {/* Combined Service Information and Service Settings */}
      {
      <Stack spacing={2}>
        {networkError && (
          <Alert severity="error">
            {networkError}
          </Alert>
        )}

        {/* Download Button Section - Similar to Points of Interest */}
        <Box>
          <Button
            variant="contained"
            color="primary"
            onClick={handleDownload}
            disabled={!jwtToken.trim() || !selectedProductId || !serviceDate || isDownloading || isDownloadingODB}
            fullWidth
            startIcon={isDownloading || isDownloadingODB ? <CircularProgress size={16} /> : <Icon name="file-arrow-down" />}
            sx={(theme) => drawerPrimaryActionSx(theme)}
          >
            {isDownloading || isDownloadingODB ? t('common.downloading') : t('serviceInfo.downloadButton')}
          </Button>
        </Box>

        {(isDownloading || isDownloadingODB) && (
          <Box>
            <LinearProgress />
            <Typography variant="caption" sx={{ mt: 0.5, display: 'block', textAlign: 'left' }}>
              {isDownloading && isDownloadingODB ? t('serviceInfo.loadingBoth') :
               isDownloading ? t('serviceInfo.loadingFR') :
               t('serviceInfo.loadingODB')}
            </Typography>
          </Box>
        )}

        {downloadError && (
          <Alert severity="error">
            <AlertTitle>{t('common.frDownloadFailed')}</AlertTitle>
            {downloadError}
          </Alert>
        )}

        {downloadErrorODB && (
          <Alert severity="error">
            <AlertTitle>{t('common.odbDownloadFailed')}</AlertTitle>
            {downloadErrorODB}
          </Alert>
        )}

        {networkSettingsError && (
          <Alert severity="warning">
            <AlertTitle>{t('serviceInfo.networkSettings')}</AlertTitle>
            {networkSettingsError}
          </Alert>
        )}

        {isImporting && (
          <Box>
            <LinearProgress variant="determinate" value={importProgress} />
            <Typography variant="caption" sx={{ mt: 0.5, display: 'block', textAlign: 'left' }}>
              {importStep} ({Math.round(importProgress)}%)
            </Typography>
          </Box>
        )}

        {importError && (
          <Alert severity="error">
            <AlertTitle>{t('common.importFailed')}</AlertTitle>
            {importError}
          </Alert>
        )}

        {importResults && (
          <Alert severity="success">
            <AlertTitle>{t('common.importComplete')}</AlertTitle>
            {importResults.stops && (
              <Typography variant="body2" component="div">
                {t('serviceInfo.results.stops', {
                  created: importResults.stops.created,
                  updated: importResults.stops.updated,
                  skipped: importResults.stops.skipped,
                })}
              </Typography>
            )}
            {importResults.routes && (
              <Typography variant="body2" component="div">
                {t('serviceInfo.results.routes', {
                  created: importResults.routes.created,
                  skipped: importResults.routes.skipped,
                })}
              </Typography>
            )}
            {importResults.vehicles && (
              <Typography variant="body2" component="div">
                {t('serviceInfo.results.vehicles', { count: importResults.vehicles.created })}
              </Typography>
            )}
            {importResults.servicesCreated !== undefined && (
              <Typography variant="body2" component="div">
                {t('serviceInfo.results.serviceSettings', { count: importResults.servicesCreated })}
              </Typography>
            )}
          </Alert>
        )}

        {/* Dynamic Layout: Show FR and/or ODB Service Information */}
        {(operationsSolutions || odbServiceInfoList.length > 0) && (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            {/* Fixed Route Service Information Section */}
            {operationsSolutions && (
              <Box sx={{ flex: odbServiceInfoList.length > 0 ? 1 : 'none', minWidth: 0, width: odbServiceInfoList.length > 0 ? 'auto' : '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="h6">{t('serviceInfo.fixedRouteServiceInformation')}</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />

        {/* FR Service Information Summary */}
        {operationsSolutions && serviceEntries.length > 0 && (
          <>
            <Paper sx={{ p: 2, bgcolor: 'background.default', mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                {t('serviceInfo.importOptions')}
              </Typography>
              <Stack spacing={1}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedFilters.has('routes')}
                      onChange={(e) => {
                        const newFilters = new Set(selectedFilters);
                        if (e.target.checked) {
                          newFilters.add('routes');
                        } else {
                          newFilters.delete('routes');
                        }
                        setSelectedFilters(newFilters);
                      }}
                    />
                  }
                  label={t('serviceInfo.routesCount', { count: totalRoutes })}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedFilters.has('vehicles')}
                      onChange={(e) => {
                        const newFilters = new Set(selectedFilters);
                        if (e.target.checked) {
                          newFilters.add('vehicles');
                        } else {
                          newFilters.delete('vehicles');
                        }
                        setSelectedFilters(newFilters);
                      }}
                    />
                  }
                  label={t('serviceInfo.vehiclesCount', { count: totalVehicles })}
                />
              </Stack>
              <Button
                variant="contained"
                color="primary"
                size="medium"
                onClick={handleDownloadServiceInfo}
                disabled={
                  (selectedFilters.size === 0 && selectedServiceIds.size === 0) || 
                  isImporting ||
                  (selectedFilters.size > 0 && (!operationsSolutions || serviceEntries.length === 0)) ||
                  (selectedServiceIds.size > 0 && previewServices.length === 0)
                }
                fullWidth
                sx={(theme) => ({ mt: 2, ...drawerPrimaryActionSx(theme) })}
                startIcon={isImporting ? <CircularProgress size={16} /> : <Icon name="circle-check" />}
              >
                {isImporting ? t('common.importing') :
                  (() => {
                    const parts: string[] = [];
                    if (selectedFilters.has('routes')) parts.push(t('serviceInfo.importPartRoutes'));
                    if (selectedFilters.has('vehicles')) parts.push(t('serviceInfo.importPartVehicles'));
                    if (selectedServiceIds.size > 0) parts.push(t('serviceInfo.importPartServiceSettings', { count: selectedServiceIds.size }));
                    return t('serviceInfo.importButtonDynamic', { parts: parts.join(t('serviceInfo.importPartJoiner')) });
                  })()}
              </Button>
              {selectedProduct && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  {t('serviceInfo.networkDateTimezone', { name: selectedProduct.productName, date: serviceDate, timezone: effectiveTimezone })}
                </Typography>
              )}
            </Paper>

            {/* Service Entries List */}
            <Accordion
              expanded={expandedSections.has('serviceEntries')}
              onChange={() => {
                const newExpanded = new Set(expandedSections);
                if (expandedSections.has('serviceEntries')) {
                  newExpanded.delete('serviceEntries');
                } else {
                  newExpanded.add('serviceEntries');
                }
                setExpandedSections(newExpanded);
              }}
            >
              <AccordionSummary expandIcon={<Icon size={20} name="chevron-down" />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Icon size={20} name="circle-info" />
                  <Typography variant="subtitle2" fontWeight={600}>
                    {t('serviceInfo.serviceEntries', { count: serviceEntries.length })}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1}>
                {serviceEntries.map((entry: UATOperationsSolution) => {
                  const isExpanded = expandedServiceIds.has(entry.id);
                  const shifts = entry.shiftSolutions || [];
                  const uniqueRoutes = new Set(shifts.map(s => s.route?.id).filter(Boolean));
                  const uniqueVehicles = new Set(shifts.map(s => s.vehicle?.id).filter(Boolean));
                  const uniqueDrivers = new Set(shifts.map(s => s.driver?.id).filter(Boolean));

                  return (
                    <Accordion
                      key={entry.id}
                      expanded={isExpanded}
                      onChange={() => {
                        const newExpanded = new Set(expandedServiceIds);
                        if (isExpanded) {
                          newExpanded.delete(entry.id);
                        } else {
                          newExpanded.add(entry.id);
                        }
                        setExpandedServiceIds(newExpanded);
                      }}
                    >
                      <AccordionSummary expandIcon={<Icon size={20} name="chevron-down" />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                          <Icon size={20} name="circle-info" />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" fontWeight={500}>
                              {entry.name || t('serviceInfo.serviceFallbackName', { id: entry.id.substring(0, 8) })}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatTimeInNetworkTimezone(entry.startDate, true)} - {formatTimeInNetworkTimezone(entry.endDate, true)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Chip label={t('serviceInfo.shifts', { count: shifts.length })} size="small" />
                            <Chip label={t('serviceInfo.routes', { count: uniqueRoutes.size })} size="small" />
                            <Chip label={t('serviceInfo.vehicles', { count: uniqueVehicles.size })} size="small" />
                          </Box>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>{t('serviceInfo.shiftHeaders.shiftId')}</TableCell>
                                <TableCell>{t('serviceInfo.shiftHeaders.route')}</TableCell>
                                <TableCell>{t('serviceInfo.shiftHeaders.vehicle')}</TableCell>
                                <TableCell>{t('serviceInfo.shiftHeaders.driver')}</TableCell>
                                <TableCell>{t('serviceInfo.shiftHeaders.start')}</TableCell>
                                <TableCell>{t('serviceInfo.shiftHeaders.end')}</TableCell>
                                <TableCell>{t('serviceInfo.shiftHeaders.status')}</TableCell>
                                <TableCell>{t('serviceInfo.shiftHeaders.type')}</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {shifts.map((shift) => (
                                <TableRow key={shift.id}>
                                  <TableCell>
                                    <Typography variant="caption" fontFamily="monospace">
                                      {shift.id.substring(0, 8)}...
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    {shift.route?.label || shift.route?.id || t('serviceInfo.na')}
                                  </TableCell>
                                  <TableCell>
                                    {shift.vehicle?.name || shift.vehicle?.id || t('serviceInfo.na')}
                                  </TableCell>
                                  <TableCell>
                                    {shift.driver?.displayName || shift.driver?.id || t('serviceInfo.na')}
                                  </TableCell>
                                  <TableCell>
                                    {shift.startDate ? formatTimeInNetworkTimezone(shift.startDate) : t('serviceInfo.na')}
                                  </TableCell>
                                  <TableCell>
                                    {shift.endDate ? formatTimeInNetworkTimezone(shift.endDate) : t('serviceInfo.na')}
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={shift.status || t('serviceInfo.unknown')}
                                      size="small"
                                      color={shift.status === 'active' ? 'success' : 'default'}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    {shift.shiftType || t('serviceInfo.na')}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
                </Stack>
              </AccordionDetails>
            </Accordion>

            {/* Network Settings Section */}
            {networkSettings && networkSettings.data.networks.length > 0 && (
              <Accordion
                expanded={expandedSections.has('networkSettings')}
                onChange={() => {
                  const newExpanded = new Set(expandedSections);
                  if (expandedSections.has('networkSettings')) {
                    newExpanded.delete('networkSettings');
                  } else {
                    newExpanded.add('networkSettings');
                  }
                  setExpandedSections(newExpanded);
                }}
              >
                <AccordionSummary expandIcon={<Icon size={20} name="chevron-down" />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Icon size={20} name="gear" />
                    <Typography variant="subtitle2" fontWeight={600}>
                      {t('serviceInfo.networkSettings')}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={2}>
                    {networkSettings.data.networks.map((network, idx) => {
                      const settings = network.networkSettings;
                      return (
                        <Box key={settings.id || idx}>
                          {/* Basic Settings */}
                          <Accordion>
                            <AccordionSummary expandIcon={<Icon size={20} name="chevron-down" />}>
                              <Typography variant="body2" fontWeight={500}>
                                {t('serviceInfo.basicSettings')}
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <TableContainer>
                                <Table size="small">
                                  <TableBody>
                                    <TableRow>
                                      <TableCell><strong>{t('serviceInfo.fields.networkId')}</strong></TableCell>
                                      <TableCell>{settings.id}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                      <TableCell><strong>{t('serviceInfo.fields.databaseId')}</strong></TableCell>
                                      <TableCell>{settings.dbId}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                      <TableCell><strong>{t('serviceInfo.fields.timezone')}</strong></TableCell>
                                      <TableCell>{settings.timezone}</TableCell>
                                    </TableRow>
                                    {settings.address && (
                                      <>
                                        <TableRow>
                                          <TableCell><strong>{t('serviceInfo.fields.address')}</strong></TableCell>
                                          <TableCell>{settings.address.label}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                          <TableCell><strong>{t('serviceInfo.fields.coordinates')}</strong></TableCell>
                                          <TableCell>{settings.address.latitude.toFixed(6)}, {settings.address.longitude.toFixed(6)}</TableCell>
                                        </TableRow>
                                        {settings.address.placeId && (
                                          <TableRow>
                                            <TableCell><strong>{t('serviceInfo.fields.placeId')}</strong></TableCell>
                                            <TableCell>{settings.address.placeId}</TableCell>
                                          </TableRow>
                                        )}
                                      </>
                                    )}
                                    <TableRow>
                                      <TableCell><strong>{t('serviceInfo.fields.userManagement')}</strong></TableCell>
                                      <TableCell>{settings.userManagement}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                      <TableCell><strong>{t('serviceInfo.fields.dateRideRestriction')}</strong></TableCell>
                                      <TableCell>{t('serviceInfo.fields.dateRideRestrictionValue', { value: settings.dateRideRestriction })}</TableCell>
                                    </TableRow>
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </AccordionDetails>
                          </Accordion>

                          {/* Format & Localization */}
                          <Accordion>
                            <AccordionSummary expandIcon={<Icon size={20} name="chevron-down" />}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Icon d={ICON_FALLBACKS.Language} size={20} />
                                <Typography variant="body2" fontWeight={500}>
                                  {t('serviceInfo.formatLocalization')}
                                </Typography>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                              <TableContainer>
                                <Table size="small">
                                  <TableBody>
                                    <TableRow>
                                      <TableCell><strong>{t('serviceInfo.fields.dateFormat')}</strong></TableCell>
                                      <TableCell>{settings.dateFormat || t('serviceInfo.na')}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                      <TableCell><strong>{t('serviceInfo.fields.timeFormat')}</strong></TableCell>
                                      <TableCell>{settings.timeFormat || t('serviceInfo.na')}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                      <TableCell><strong>{t('serviceInfo.fields.longDateFormat')}</strong></TableCell>
                                      <TableCell>{settings.dateFormatLong || t('serviceInfo.na')}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                      <TableCell><strong>{t('serviceInfo.fields.longTimeFormat')}</strong></TableCell>
                                      <TableCell>{settings.timeFormatLong || t('serviceInfo.na')}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                      <TableCell><strong>{t('serviceInfo.fields.measurementUnit')}</strong></TableCell>
                                      <TableCell>{settings.measurementUnit || t('serviceInfo.na')}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                      <TableCell><strong>{t('serviceInfo.fields.locale')}</strong></TableCell>
                                      <TableCell>{settings.locale || t('serviceInfo.na')}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                      <TableCell><strong>{t('serviceInfo.fields.currency')}</strong></TableCell>
                                      <TableCell>{settings.currencyCode || t('serviceInfo.na')}</TableCell>
                                    </TableRow>
                                    <TableRow>
                                      <TableCell><strong>{t('serviceInfo.fields.mobileCountryCode')}</strong></TableCell>
                                      <TableCell>{t('serviceInfo.fields.mobileCountryCodeValue', { code: settings.mobileCountryCode || t('serviceInfo.na'), dial: settings.mobileCountryDialCode || t('serviceInfo.na') })}</TableCell>
                                    </TableRow>
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </AccordionDetails>
                          </Accordion>

                          {/* Vehicle Capacities */}
                          {settings.maxVehicleCapacities && (
                            <Accordion>
                              <AccordionSummary expandIcon={<Icon size={20} name="chevron-down" />}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Icon name="truck" size={20} />
                                  <Typography variant="body2" fontWeight={500}>
                                    {t('serviceInfo.vehicleCapacities')}
                                  </Typography>
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails>
                                <TableContainer>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell><strong>{t('serviceInfo.capacityHeaders.serviceType')}</strong></TableCell>
                                        <TableCell align="right"><strong>{t('serviceInfo.capacityHeaders.standard')}</strong></TableCell>
                                        <TableCell align="right"><strong>{t('serviceInfo.capacityHeaders.wheelchair')}</strong></TableCell>
                                        <TableCell align="right"><strong>{t('serviceInfo.capacityHeaders.luggage')}</strong></TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {settings.maxVehicleCapacities.fixedRoute && (
                                        <TableRow>
                                          <TableCell><strong>{t('serviceInfo.fixedRoute')}</strong></TableCell>
                                          <TableCell align="right">{settings.maxVehicleCapacities.fixedRoute.standard}</TableCell>
                                          <TableCell align="right">{settings.maxVehicleCapacities.fixedRoute.wheelchair}</TableCell>
                                          <TableCell align="right">{settings.maxVehicleCapacities.fixedRoute.luggage}</TableCell>
                                        </TableRow>
                                      )}
                                      {settings.maxVehicleCapacities.onDemand && (
                                        <TableRow>
                                          <TableCell><strong>{t('serviceInfo.onDemand')}</strong></TableCell>
                                          <TableCell align="right">{settings.maxVehicleCapacities.onDemand.standard}</TableCell>
                                          <TableCell align="right">{settings.maxVehicleCapacities.onDemand.wheelchair}</TableCell>
                                          <TableCell align="right">{settings.maxVehicleCapacities.onDemand.luggage}</TableCell>
                                        </TableRow>
                                      )}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                                {settings.luggageSizePerRideLimit && (
                                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                    {t('serviceInfo.luggagePerRide', { value: settings.luggageSizePerRideLimit })}
                                  </Typography>
                                )}
                              </AccordionDetails>
                            </Accordion>
                          )}

                          {/* Payment Settings */}
                          {settings.payments && (
                            <Accordion>
                              <AccordionSummary expandIcon={<Icon size={20} name="chevron-down" />}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Icon name="credit-card" size={20} />
                                  <Typography variant="body2" fontWeight={500}>
                                    {t('serviceInfo.paymentSettings')}
                                  </Typography>
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails>
                                <TableContainer>
                                  <Table size="small">
                                    <TableBody>
                                      <TableRow>
                                        <TableCell><strong>{t('serviceInfo.fields.paymentsEnabled')}</strong></TableCell>
                                        <TableCell>
                                          <Chip
                                            label={settings.payments.enabled ? t('serviceInfo.yes') : t('serviceInfo.no')}
                                            size="small"
                                            color={settings.payments.enabled ? 'success' : 'default'}
                                          />
                                        </TableCell>
                                      </TableRow>
                                      {settings.payments.methods && settings.payments.methods.length > 0 && (
                                        <TableRow>
                                          <TableCell><strong>{t('serviceInfo.fields.paymentMethods')}</strong></TableCell>
                                          <TableCell>
                                            {settings.payments.methods.map((method: any, idx: number) => {
                                              // Handle both string and object payment methods
                                              const methodLabel = typeof method === 'string' 
                                                ? method 
                                                : method?.type || method?.provider || method?.id || JSON.stringify(method);
                                              return (
                                                <Chip key={idx} label={methodLabel} size="small" sx={{ mr: 0.5 }} />
                                              );
                                            })}
                                          </TableCell>
                                        </TableRow>
                                      )}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </AccordionDetails>
                            </Accordion>
                          )}

                          {/* Features */}
                          {network.features && network.features.length > 0 && (
                            <Accordion>
                              <AccordionSummary expandIcon={<Icon size={20} name="chevron-down" />}>
                                <Typography variant="body2" fontWeight={500}>
                                  {t('serviceInfo.featuresTitle', { count: network.features.filter((f: any) => f.enabled).length })}
                                </Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                  {network.features.map((feature: any, idx: number) => (
                                    <Chip
                                      key={idx}
                                      label={feature.name}
                                      size="small"
                                      color={feature.enabled ? 'success' : 'default'}
                                      variant={feature.enabled ? 'filled' : 'outlined'}
                                    />
                                  ))}
                                </Box>
                              </AccordionDetails>
                            </Accordion>
                          )}

                          {/* Network Customizations */}
                          {network.networkCustomisations && network.networkCustomisations.length > 0 && (
                            <Accordion>
                              <AccordionSummary expandIcon={<Icon size={20} name="chevron-down" />}>
                                <Typography variant="body2" fontWeight={500}>
                                  {t('serviceInfo.networkCustomizations', { count: network.networkCustomisations.length })}
                                </Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                <TableContainer>
                                  <Table size="small">
                                    <TableHead>
                                      <TableRow>
                                        <TableCell><strong>{t('serviceInfo.customHeaders.key')}</strong></TableCell>
                                        <TableCell><strong>{t('serviceInfo.customHeaders.value')}</strong></TableCell>
                                        <TableCell align="center"><strong>{t('serviceInfo.customHeaders.enabled')}</strong></TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {network.networkCustomisations.map((custom: any, idx: number) => (
                                        <TableRow key={idx}>
                                          <TableCell>{custom.key}</TableCell>
                                          <TableCell>
                                            <Typography variant="caption" sx={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                              {custom.value || t('serviceInfo.emptyValue')}
                                            </Typography>
                                          </TableCell>
                                          <TableCell align="center">
                                            <Chip
                                              label={custom.enabled ? t('serviceInfo.yes') : t('serviceInfo.no')}
                                              size="small"
                                              color={custom.enabled ? 'success' : 'default'}
                                            />
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </AccordionDetails>
                            </Accordion>
                          )}

                          {/* Accessibility & Requirements */}
                          <Accordion>
                            <AccordionSummary expandIcon={<Icon size={20} name="chevron-down" />}>
                              <Typography variant="body2" fontWeight={500}>
                                {t('serviceInfo.accessibilityRequirements')}
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <TableContainer>
                                <Table size="small">
                                  <TableBody>
                                    <TableRow>
                                      <TableCell><strong>{t('serviceInfo.fields.accessibilityRequirements')}</strong></TableCell>
                                      <TableCell>
                                        <Chip
                                          label={settings.accessibilityRequirementsEnabled ? t('serviceInfo.enabled') : t('serviceInfo.disabled')}
                                          size="small"
                                          color={settings.accessibilityRequirementsEnabled ? 'success' : 'default'}
                                        />
                                      </TableCell>
                                    </TableRow>
                                    <TableRow>
                                      <TableCell><strong>{t('serviceInfo.fields.mobilityRequirements')}</strong></TableCell>
                                      <TableCell>
                                        <Chip
                                          label={settings.mobilityRequirementsEnabled ? t('serviceInfo.enabled') : t('serviceInfo.disabled')}
                                          size="small"
                                          color={settings.mobilityRequirementsEnabled ? 'success' : 'default'}
                                        />
                                      </TableCell>
                                    </TableRow>
                                    {settings.driverDirectionsMapProvider && (
                                      <TableRow>
                                        <TableCell><strong>{t('serviceInfo.fields.driverDirectionsProvider')}</strong></TableCell>
                                        <TableCell>{settings.driverDirectionsMapProvider}</TableCell>
                                      </TableRow>
                                    )}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </AccordionDetails>
                          </Accordion>
                        </Box>
                      );
                    })}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            )}
          </>
        )}

              </Box>
            )}

            {/* On-Demand Service Information Section */}
            {odbServiceInfoList.length > 0 && (
              <Box sx={{ flex: operationsSolutions ? 1 : 'none', minWidth: 0, width: operationsSolutions ? 'auto' : '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="h6">{t('serviceInfo.onDemandServiceInformation')}</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />

            {odbServiceInfoList.length > 0 ? (
              <Stack spacing={2}>
                {/* ODB Service Information Preview */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {t('serviceInfo.odbPreview', { count: odbServiceInfoList.length })}
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => {
                        if (selectedODBServiceInfoIds.size === odbServiceInfoList.length) {
                          setSelectedODBServiceInfoIds(new Set());
                        } else {
                          setSelectedODBServiceInfoIds(new Set(odbServiceInfoList.map(si => si.id)));
                        }
                      }}
                      disabled={isImportingODB}
                      sx={(theme) => drawerTertiaryActionSx(theme)}
                    >
                      {selectedODBServiceInfoIds.size === odbServiceInfoList.length ? t('common.deselectAll') : t('common.selectAll')}
                    </Button>
                  </Box>

                  {/* Search */}
                  <Box sx={{ mb: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder={t('serviceInfo.searchODBPlaceholder')}
                      value={odbServiceInfoSearchQuery}
                      onChange={(e) => setOdbServiceInfoSearchQuery(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Icon size={20} name="magnifying-glass" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  {/* Filtered ODB Service Information List */}
                  <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {odbServiceInfoList
                      .filter(si => {
                        if (!odbServiceInfoSearchQuery.trim()) return true;
                        const query = odbServiceInfoSearchQuery.toLowerCase();
                        return (
                          si.name?.toLowerCase().includes(query) ||
                          si.description?.toLowerCase().includes(query) ||
                          si.serviceType?.toLowerCase().includes(query) ||
                          si.networkId?.toLowerCase().includes(query)
                        );
                      })
                      .map((si) => (
                        <Accordion
                          key={si.id}
                          expanded={expandedODBServiceInfoIds.has(si.id)}
                          onChange={() => {
                            const newExpanded = new Set(expandedODBServiceInfoIds);
                            if (expandedODBServiceInfoIds.has(si.id)) {
                              newExpanded.delete(si.id);
                            } else {
                              newExpanded.add(si.id);
                            }
                            setExpandedODBServiceInfoIds(newExpanded);
                          }}
                        >
                          <AccordionSummary expandIcon={<Icon size={20} name="chevron-down" />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                              <Checkbox
                                checked={selectedODBServiceInfoIds.has(si.id)}
                                onChange={(e) => {
                                  const newSelected = new Set(selectedODBServiceInfoIds);
                                  if (e.target.checked) {
                                    newSelected.add(si.id);
                                  } else {
                                    newSelected.delete(si.id);
                                  }
                                  setSelectedODBServiceInfoIds(newSelected);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                disabled={isImportingODB}
                              />
                              <Typography variant="body2" sx={{ flex: 1 }}>
                                {si.name || t('serviceInfo.unnamedService')}
                              </Typography>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Stack spacing={1}>
                              <Typography variant="caption" color="text.secondary">
                                <strong>ID:</strong> {si.id}
                              </Typography>
                              {si.description && (
                                <Typography variant="caption" color="text.secondary">
                                  <strong>Description:</strong> {si.description}
                                </Typography>
                              )}
                              <Typography variant="caption" color="text.secondary">
                                <strong>Service Type:</strong> {si.serviceType}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                <strong>Date Range:</strong> {si.startDate} {si.endDate ? `to ${si.endDate}` : '(ongoing)'}
                              </Typography>
                              {si.bookingStartDate && (
                                <Typography variant="caption" color="text.secondary">
                                  <strong>Booking Start:</strong> {new Date(si.bookingStartDate).toLocaleString()}
                                </Typography>
                              )}
                              <Typography variant="caption" color="text.secondary">
                                <strong>Timezone:</strong> {si.timezone}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                <strong>Ride Lock Time Offset:</strong> {si.rideLockTimeOffset} minutes
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                <strong>Max Companions:</strong> {si.maxCompanions}
                              </Typography>
                              {si.serviceFlags && (
                                <Box>
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                    <strong>Service Flags:</strong>
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {si.serviceFlags.hubsEnabled && <Chip label={t('serviceInfo.serviceFlags.hubsEnabled')} size="small" />}
                                    {si.serviceFlags.geofencesEnabled && <Chip label={t('serviceInfo.serviceFlags.geofencesEnabled')} size="small" />}
                                    {si.serviceFlags.breaksEnabled && <Chip label={t('serviceInfo.serviceFlags.breaksEnabled')} size="small" />}
                                    {si.serviceFlags.zonesEnabled && <Chip label={t('serviceInfo.serviceFlags.zonesEnabled')} size="small" />}
                                    {si.serviceFlags.isHubToHub && <Chip label={t('serviceInfo.serviceFlags.hubToHub')} size="small" />}
                                  </Box>
                                </Box>
                              )}
                              {si.networkId && (
                                <Typography variant="caption" color="text.secondary">
                                  <strong>Network ID:</strong> {si.networkId}
                                </Typography>
                              )}
                              {si.capacities && si.capacities.length > 0 && (
                                <Box>
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, mt: 1 }}>
                                    <strong>Capacities:</strong>
                                  </Typography>
                                  <TableContainer>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell><strong>Type</strong></TableCell>
                                          <TableCell align="right"><strong>Once Off Time</strong></TableCell>
                                          <TableCell align="right"><strong>Time Per Unit</strong></TableCell>
                                          <TableCell align="center"><strong>Movable</strong></TableCell>
                                          <TableCell align="center"><strong>Dependant</strong></TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {si.capacities.map((cap) => (
                                          <TableRow key={cap.id}>
                                            <TableCell>
                                              <Chip 
                                                label={cap.type.name} 
                                                size="small" 
                                                sx={{ textTransform: 'capitalize' }}
                                              />
                                            </TableCell>
                                            <TableCell align="right">{cap.onceOffTime}s</TableCell>
                                            <TableCell align="right">{cap.timePerUnit}s</TableCell>
                                            <TableCell align="center">
                                              <Chip 
                                                label={cap.movable ? t('serviceInfo.yes') : t('serviceInfo.no')}
                                                size="small"
                                                color={cap.movable ? 'success' : 'default'}
                                              />
                                            </TableCell>
                                            <TableCell align="center">
                                              <Chip 
                                                label={cap.isDependant ? t('serviceInfo.yes') : t('serviceInfo.no')}
                                                size="small"
                                                color={cap.isDependant ? 'warning' : 'default'}
                                              />
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                </Box>
                              )}
                            </Stack>
                          </AccordionDetails>
                        </Accordion>
                      ))}
                  </Box>
                </Box>

                {/* ODB Import Button */}
                <Button
                  variant="contained"
                  color="primary"
                  size="medium"
                  onClick={handleImportODB}
                  disabled={selectedODBServiceInfoIds.size === 0 || isImportingODB}
                  fullWidth
                  startIcon={isImportingODB ? <CircularProgress size={16} /> : <Icon name="circle-check" />}
                  sx={(theme) => drawerPrimaryActionSx(theme)}
                >
                  {isImportingODB ? t('common.importing') : t('serviceInfo.importODBButton', { count: selectedODBServiceInfoIds.size })}
                </Button>

                {isImportingODB && (
                  <Box>
                    <LinearProgress variant="determinate" value={importProgressODB} />
                    <Typography variant="caption" sx={{ mt: 0.5, display: 'block', textAlign: 'left' }}>
                      {importStepODB || t('serviceInfo.importingServiceInfo')} ({Math.round(importProgressODB)}%)
                    </Typography>
                  </Box>
                )}

                {importErrorODB && (
                  <Alert severity="error">
                    <AlertTitle>{t('common.odbImportFailed')}</AlertTitle>
                    {importErrorODB}
                  </Alert>
                )}

                {importResultsODB && importResultsODB.length > 0 && (
                  <Alert severity="success">
                    <AlertTitle>{t('common.odbImportComplete')}</AlertTitle>
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      <strong>{t('serviceInfo.odbResultLabel')}</strong>{' '}
                      {t('serviceInfo.odbResultSummary', {
                        created: importResultsODB.filter(r => r.action === 'created').length,
                        skipped: importResultsODB.filter(r => r.action === 'skipped').length,
                      })}
                    </Typography>
                  </Alert>
                )}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('serviceInfo.odbEmptyMessage')}
              </Typography>
            )}
              </Box>
            )}
          </Box>
        )}

        {/* Service Settings Section */}
        {(() => {
          if (previewServices.length > 0) {
            console.log('[ServiceInformationDownloadSection] Rendering Service Settings section with', previewServices.length, 'services');
            return (
              <Box>
                <Typography variant="h6" gutterBottom>
                  {t('serviceInfo.serviceSettingsCount', { count: previewServices.length })}
                </Typography>
                <Divider sx={{ mb: 2 }} />

            <Paper sx={{ p: 2, bgcolor: 'background.default', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={600}>
                  {t('serviceInfo.previewServices', { count: previewServices.length })}
                </Typography>
                <Button
                  size="small"
                  onClick={() => {
                    if (selectedServiceIds.size === previewServices.length) {
                      setSelectedServiceIds(new Set());
                    } else {
                      setSelectedServiceIds(new Set(previewServices.map(s => s.id)));
                    }
                  }}
                  disabled={isImporting}
                  sx={(theme) => drawerTertiaryActionSx(theme)}
                >
                  {selectedServiceIds.size === previewServices.length ? t('common.deselectAll') : t('common.selectAll')}
                </Button>
              </Box>

              <TextField
                fullWidth
                size="small"
                placeholder={t('serviceInfo.searchServicesPlaceholder')}
                value={serviceSearchQuery}
                onChange={(e) => setServiceSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon size={20} name="magnifying-glass" />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />
              
              <Box sx={{ maxHeight: '300px', overflowY: 'auto' }}>
                {previewServices
                  .filter(s => {
                    if (!serviceSearchQuery.trim()) return true;
                    const query = serviceSearchQuery.toLowerCase();
                    return (
                      s.name?.toLowerCase().includes(query) ||
                      s.description?.toLowerCase().includes(query) ||
                      s.serviceType?.toLowerCase().includes(query)
                    );
                  })
                  .map((service) => (
                    <Box key={service.id} sx={{ mb: 1, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedServiceIds.has(service.id)}
                            onChange={(e) => {
                              const newSelected = new Set(selectedServiceIds);
                              if (e.target.checked) {
                                newSelected.add(service.id);
                              } else {
                                newSelected.delete(service.id);
                              }
                              setSelectedServiceIds(newSelected);
                            }}
                            disabled={isImporting}
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {service.name}
                            </Typography>
                            {service.description && (
                              <Typography variant="caption" color="text.secondary">
                                {service.description}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </Box>
                  ))}
              </Box>
            </Paper>
            
            {/* Import button for Service Settings */}
            <Button
              variant="contained"
              color="primary"
              size="medium"
              onClick={handleDownloadServiceInfo}
              disabled={
                selectedServiceIds.size === 0 || 
                isImporting ||
                previewServices.length === 0
              }
              fullWidth
              sx={(theme) => ({ mt: 2, ...drawerPrimaryActionSx(theme) })}
              startIcon={isImporting ? <CircularProgress size={16} /> : <Icon name="circle-check" />}
            >
              {isImporting ? t('common.importing') :
                selectedServiceIds.size > 0
                  ? t('serviceInfo.importServiceSettingsButton', { count: selectedServiceIds.size })
                  : t('serviceInfo.selectServiceSettingsToImport')}
            </Button>
          </Box>
            );
          }
          return null;
        })()}

        {/* Show message if nothing is available */}
        {!operationsSolutions && odbServiceInfoList.length === 0 && previewServices.length === 0 && (
          <Alert severity="info">
            No service information or service settings found for the selected date and network. Click "Download" to fetch data.
          </Alert>
        )}
      </Stack>
      }
    </Box>
  );
};

export default ServiceInformationDownloadSection;

